import { Injectable } from '@angular/core';
import { EventEmitter } from '@angular/core';
import { FullscreenToggle } from '../../../utils/fullscreen-toggle';

/**
 * Manages video playback controls state for the bunny-player.
 * Component-level provider -- each bunny-player instance gets its own instance.
 *
 * Uses DOM registration pattern: component passes element references after view init,
 * service operates on them without holding Angular-specific references.
 *
 * @requirements
 * - REQ-VIDEO-101: Video playback controls
 * - REQ-VIDEO-102: Progress tracking display
 */
@Injectable()
export class BunnyPlayerControlsService {
  // Progress tracking
  currentTime: number = 0;
  duration: number = 0;
  displayDuration: number = 0;
  showCustomProgress: boolean = false;
  watchedPercentage: number = 0;
  currentPercentage: number = 0;
  isSeekingForward: boolean = false;
  private wasPlayingBeforeSeek: boolean = false;
  private wasPlayingOrEndedBeforeSeek: boolean = false; // Tracks play state continuously (before browser pauses for seek)
  private maxWatchedTime: number = 0; // Track furthest point watched during current session

  // Audio / display
  isMuted: boolean = false;
  volume: number = 100;
  isFullscreen: boolean = false;

  // Play overlay
  showPlayOverlay: boolean = true;
  hasUserStartedVideo: boolean = false;

  // Controls auto-hide
  controlsVisible: boolean = true;
  private controlsHideTimeout: any = null;
  private readonly CONTROLS_HIDE_DELAY = 2500;

  // Iframe progress tracking
  private iframeProgressInterval: any = null;
  private iframePlayStartTime: number = 0;
  private iframeAccumulatedTime: number = 0;
  private isIframePlaying: boolean = false;

  // DOM references (registered from component)
  private videoElement: HTMLVideoElement | null = null;
  private playerContainer: HTMLElement | null = null;
  private progressBarElement: HTMLElement | null = null;

  // KAN-620: element fullscreen on the player container where supported, the
  // native iOS video player on iPhone; isFullscreen mirrors browser events.
  private readonly fullscreen = new FullscreenToggle({
    target: () => this.videoElement?.parentElement,
    fallbackVideo: () => this.videoElement,
    onChange: isFullscreen => {
      this.isFullscreen = isFullscreen;
      this.markForCheck?.();
    }
  });

  // Callback references from component (for @Output emitters)
  private emitTimeUpdate: ((data: { currentTime: number; duration: number }) => void) | null = null;
  private emitVideoLoaded: ((data: { duration: number }) => void) | null = null;
  private emitUserEngaged: (() => void) | null = null;
  private emitVideoEnded: (() => void) | null = null;

  // Reference to the speed service's showSpeedMenu for scheduleControlsHide logic
  private getShowSpeedMenu: (() => boolean) | null = null;
  private markForCheck: (() => void) | null = null;

  registerElements(
    videoElement: HTMLVideoElement | null,
    playerContainer: HTMLElement | null,
    progressBarElement: HTMLElement | null
  ): void {
    this.videoElement = videoElement;
    this.playerContainer = playerContainer;
    this.progressBarElement = progressBarElement;
    this.maxWatchedTime = 0; // Reset for new video
  }

  registerCallbacks(callbacks: {
    emitTimeUpdate: (data: { currentTime: number; duration: number }) => void;
    emitVideoLoaded: (data: { duration: number }) => void;
    emitUserEngaged: () => void;
    emitVideoEnded: () => void;
    getShowSpeedMenu: () => boolean;
    markForCheck: () => void;
  }): void {
    this.emitTimeUpdate = callbacks.emitTimeUpdate;
    this.emitVideoLoaded = callbacks.emitVideoLoaded;
    this.emitUserEngaged = callbacks.emitUserEngaged;
    this.emitVideoEnded = callbacks.emitVideoEnded;
    this.getShowSpeedMenu = callbacks.getShowSpeedMenu;
    this.markForCheck = callbacks.markForCheck;
  }

  /**
   * Get playing state from video element (canonical source)
   */
  get isPlaying(): boolean {
    return !(this.videoElement?.paused ?? true);
  }

  /**
   * Show controls and schedule auto-hide
   */
  showControls(): void {
    this.controlsVisible = true;
    this.scheduleControlsHide();
  }

  /**
   * Schedule controls to hide after delay (only if video is playing)
   */
  scheduleControlsHide(): void {
    // Clear any existing timeout
    if (this.controlsHideTimeout) {
      clearTimeout(this.controlsHideTimeout);
    }

    const showSpeedMenu = this.getShowSpeedMenu ? this.getShowSpeedMenu() : false;

    // Only auto-hide if video is playing
    if (this.isPlaying && !showSpeedMenu) {
      this.controlsHideTimeout = setTimeout(() => {
        this.controlsVisible = false;
        this.markForCheck?.();
      }, this.CONTROLS_HIDE_DELAY);
    }
  }

  /**
   * Handle mouse/touch movement on video container
   */
  onVideoContainerMouseMove(): void {
    this.showControls();
  }

  /**
   * Handle mouse leaving video container
   */
  onVideoContainerMouseLeave(): void {
    const showSpeedMenu = this.getShowSpeedMenu ? this.getShowSpeedMenu() : false;

    if (this.isPlaying && !showSpeedMenu) {
      if (this.controlsHideTimeout) {
        clearTimeout(this.controlsHideTimeout);
      }
      this.controlsHideTimeout = setTimeout(() => {
        this.controlsVisible = false;
        this.markForCheck?.();
      }, 1500);
    }
  }

  /**
   * Handle speed menu toggle -- called by speed service via callback.
   * Keeps controls visible when speed menu is open, schedules hide when closed.
   */
  onSpeedMenuToggled(showSpeedMenu: boolean): void {
    if (showSpeedMenu) {
      this.controlsVisible = true;
      if (this.controlsHideTimeout) {
        clearTimeout(this.controlsHideTimeout);
      }
    } else {
      this.scheduleControlsHide();
    }
  }

  // Hi jon
  onTimeUpdate(event: Event, maxWatchedPosition: number): void {
    const video = event.target as HTMLVideoElement;
    this.currentTime = video.currentTime;
    this.duration = video.duration;

    // Continuously track playing state so we have it before the browser pauses for a seek
    this.wasPlayingOrEndedBeforeSeek = !video.paused || video.ended;

    // Track furthest point watched during this session
    this.maxWatchedTime = Math.max(this.maxWatchedTime, this.currentTime);

    // Update the progress display
    this.updateProgressDisplay(maxWatchedPosition);

    // Emit the timeUpdate event with current duration
    const eventData = {
      currentTime: video.currentTime,
      duration: video.duration
    };

    if (this.emitTimeUpdate) {
      this.emitTimeUpdate(eventData);
    }

    this.markForCheck?.();
  }

  onVideoLoaded(
    maxWatchedPosition: number,
    startPosition: number,
    isHlsStream: boolean,
    isBunnyUrl: boolean,
    isThumbnailView: boolean,
    playbackSpeed: number
  ): void {
    if (!this.videoElement) return;

    const video = this.videoElement;

    // Ensure video doesn't auto-play
    video.pause();

    // Store duration
    this.duration = video.duration;
    this.displayDuration = Math.ceil(this.duration);

    // Initialize internal max watched tracking from loaded position
    this.maxWatchedTime = maxWatchedPosition;

    // Emit duration immediately when video loads
    if (this.emitVideoLoaded) {
      this.emitVideoLoaded({ duration: this.duration });
    }

    // Seek to initial position.
    // KAN-306 round 2: resume MUST prefer the persisted last-paused position
    // (startPosition) over the max-watched/coverage position. These are
    // distinct concepts (MUST-PRESERVE b) -- maxWatchedPosition can be well
    // ahead of where the learner actually left off (e.g. after watching
    // ahead then rewinding to pause), and resuming at the coverage position
    // instead of the true last position was the reported bug.
    const initialPosition = startPosition > 0 ? startPosition : maxWatchedPosition;

    if (initialPosition > 0) {
      if (isHlsStream || video.readyState < 3) {
        const canplayHandler = () => {
          video.currentTime = initialPosition;
          this.currentTime = initialPosition;
          this.updateProgressDisplay(maxWatchedPosition);
          this.markForCheck?.();

          video.removeEventListener('canplay', canplayHandler);
        };

        video.addEventListener('canplay', canplayHandler, { once: true });
      } else {
        video.currentTime = initialPosition;
        this.currentTime = initialPosition;
      }
    }

    // Update progress display now that we have duration
    this.updateProgressDisplay(maxWatchedPosition);

    // Apply saved playback speed
    video.playbackRate = playbackSpeed;

    // Initialize volume and muted state
    video.volume = this.volume / 100;
    this.isMuted = video.muted;

    // Enable custom progress bar for local videos
    this.showCustomProgress = !isBunnyUrl && !isThumbnailView;
  }

  /**
   * Snap the playhead/scrubber bar to 100% when video completes.
   * Called from component's (ended) event handler.
   *
   * KAN-306 round 2: does NOT force watchedPercentage to 100. The native
   * "ended" event fires once currentTime reaches duration REGARDLESS of
   * whether the content in between was skipped -- forcing the DISPLAYED
   * "watched"/progress number to 100 here would reintroduce a competing,
   * inflated number on the player controls the instant the video ends,
   * independent of the true backend-verified coverage this ticket exists to
   * surface accurately. currentPercentage (the playhead position bar) is a
   * cosmetic snap for floating-point precision (currentTime never quite
   * reaches the exact duration value) and isn't a "watched" claim, so it
   * still snaps here.
   */
  onVideoEndedSnap(): void {
    this.currentPercentage = 100;
    // Mark that video was playing/ended so seeking after completion resumes playback
    this.wasPlayingOrEndedBeforeSeek = true;
  }

  /**
   * Update the progress display values based on current state.
   *
   * KAN-306: `watchedPercentage` (the DISPLAYED "watched"/"progress" number and
   * green bar) is driven ONLY by maxWatchedPosition -- the backend-verified
   * max-watched metric passed down from the parent's @Input. It deliberately
   * does NOT mix in the internal session playhead tracker (this.maxWatchedTime),
   * which just mirrors currentTime as the video plays/seeks forward and would
   * inflate the displayed number toward the scrubber position (the original
   * bug: player controls read "99% watched" while true coverage was 59%).
   * this.maxWatchedTime is still used separately by the seek-restriction gate
   * (onSeeking/onSeeked/seekToPosition) -- that behavior is unchanged.
   */
  updateProgressDisplay(maxWatchedPosition: number): void {
    if (this.duration > 0) {
      // Snap both bars to 100% when current time is near the end
      if (this.currentTime >= this.duration - 0.5) {
        this.currentPercentage = 100;
      } else {
        this.currentPercentage = (this.currentTime / this.displayDuration) * 100;
      }

      if (maxWatchedPosition >= this.duration - 0.5) {
        this.watchedPercentage = 100;
      } else {
        this.watchedPercentage = Math.ceil((maxWatchedPosition / this.displayDuration) * 100);
      }
    }
  }

  onSeeking(event: Event, restrictForwardSeek: boolean, maxWatchedPosition: number): void {
    if (!this.videoElement) return;

    const video = event.target as HTMLVideoElement;

    // Use the pre-tracked playing state captured during timeupdate/ended events.
    // By the time 'seeking' fires, the browser has already paused the video,
    // so reading video.paused here would always be true.
    this.wasPlayingBeforeSeek = this.wasPlayingOrEndedBeforeSeek;

    if (!restrictForwardSeek) return;

    const effectiveMaxWatched = Math.max(this.maxWatchedTime, maxWatchedPosition);

    if (video.currentTime > effectiveMaxWatched) {
      this.isSeekingForward = true;
    }
  }

  onSeeked(event: Event, restrictForwardSeek: boolean, maxWatchedPosition: number): void {
    if (!this.videoElement) return;

    const video = event.target as HTMLVideoElement;

    if (restrictForwardSeek) {
      const effectiveMaxWatched = Math.max(this.maxWatchedTime, maxWatchedPosition);

      if (this.isSeekingForward && video.currentTime > effectiveMaxWatched) {
        video.currentTime = effectiveMaxWatched;

        const notification = document.createElement('div');
        notification.className = 'fixed top-4 left-1/2 transform -translate-x-1/2';
        notification.style.zIndex = '10050';
        notification.innerHTML = `
          <div class="alert alert-warning shadow-lg">
            <span>Please watch the video to continue forward</span>
          </div>
        `;
        document.body.appendChild(notification);

        setTimeout(() => {
          notification.remove();
        }, 3000);
      }
    }

    // Resume playback if the video was playing or had ended before the seek
    if (this.wasPlayingBeforeSeek && video.paused) {
      video.play().catch(err => {
        console.warn('[BUNNY_PLAYER] Resume after seek failed:', err.message);
      });
    }

    this.isSeekingForward = false;
    this.wasPlayingBeforeSeek = false;
    this.wasPlayingOrEndedBeforeSeek = false;
  }

  togglePlayPause(): void {
    if (!this.videoElement) return;

    const video = this.videoElement;
    if (video.paused) {
      this.startVideoPlayback();
      this.scheduleControlsHide();
    } else {
      video.pause();
      this.controlsVisible = true;
      if (this.controlsHideTimeout) {
        clearTimeout(this.controlsHideTimeout);
      }
    }
  }

  /**
   * Start video playback and track user engagement
   */
  startVideoPlayback(): void {
    if (!this.videoElement) return;

    const video = this.videoElement;

    if (!this.hasUserStartedVideo) {
      this.hasUserStartedVideo = true;
      this.showPlayOverlay = false;
      if (this.emitUserEngaged) {
        this.emitUserEngaged();
      }
    }

    video.play().catch(err => {
      console.warn('[BUNNY_PLAYER] Play failed:', err.message);
    });
    this.scheduleControlsHide();
  }

  toggleMute(): void {
    if (!this.videoElement) return;

    const video = this.videoElement;
    video.muted = !video.muted;
    this.isMuted = video.muted;

    if (!video.muted) {
      video.volume = this.volume / 100;
    }
  }

  onVolumeChange(event: Event): void {
    if (!this.videoElement) return;

    const video = this.videoElement;
    video.volume = this.volume / 100;

    if (this.volume === 0) {
      this.isMuted = true;
      video.muted = true;
    } else if (video.muted) {
      this.isMuted = false;
      video.muted = false;
    }
  }

  toggleFullscreen(): void {
    if (!this.videoElement?.parentElement) return;
    this.fullscreen.toggle();
  }

  seekToPosition(
    event: MouseEvent,
    restrictForwardSeek: boolean,
    maxWatchedPosition: number
  ): void {
    if (!this.videoElement || !this.progressBarElement) return;

    const video = this.videoElement;
    const progressBar = this.progressBarElement;

    const rect = progressBar.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const percentage = (x / rect.width) * 100;
    const seekTime = (percentage / 100) * this.duration;
    const effectiveMaxWatched = Math.max(this.maxWatchedTime, maxWatchedPosition);

    // Track whether video was playing before seek (or had ended)
    const wasPlayingOrEnded = !video.paused || video.ended;

    if (restrictForwardSeek && seekTime > effectiveMaxWatched) {
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 left-1/2 transform -translate-x-1/2';
      notification.style.zIndex = '10050';
      notification.innerHTML = `
        <div class="alert alert-warning shadow-lg">
          <span>Please watch the video to continue forward</span>
        </div>
      `;
      document.body.appendChild(notification);

      setTimeout(() => {
        notification.remove();
      }, 3000);

      video.currentTime = effectiveMaxWatched;
    } else {
      video.currentTime = seekTime;
    }

    // Resume playback after seeking if video was playing or had ended
    if (wasPlayingOrEnded && video.paused) {
      video.play();
    }
  }

  /**
   * Simulate progress tracking for iframe videos
   */
  startIframeProgressTracking(): void {
    const assumedDuration = 300;

    let currentTime = this.iframeAccumulatedTime;

    if (this.emitTimeUpdate) {
      this.emitTimeUpdate({
        currentTime: currentTime,
        duration: assumedDuration
      });
    }

    this.iframeProgressInterval = setInterval(() => {
      if (this.isIframePlaying) {
        currentTime += 1;
        this.iframeAccumulatedTime = currentTime;

        if (this.emitTimeUpdate) {
          this.emitTimeUpdate({
            currentTime: currentTime,
            duration: assumedDuration
          });
        }

        if (currentTime >= assumedDuration) {
          this.isIframePlaying = false;
          clearInterval(this.iframeProgressInterval);
          if (this.emitVideoEnded) {
            this.emitVideoEnded();
          }
        }
        this.markForCheck?.();
      }
    }, 1000);

    setTimeout(() => {
      this.isIframePlaying = true;
      this.markForCheck?.();
    }, 2000);
  }

  destroy(): void {
    this.fullscreen.destroy();
    if (this.iframeProgressInterval) {
      clearInterval(this.iframeProgressInterval);
    }
    if (this.controlsHideTimeout) {
      clearTimeout(this.controlsHideTimeout);
    }
    this.videoElement = null;
    this.playerContainer = null;
    this.progressBarElement = null;
    this.emitTimeUpdate = null;
    this.emitVideoLoaded = null;
    this.emitUserEngaged = null;
    this.emitVideoEnded = null;
    this.getShowSpeedMenu = null;
    this.markForCheck = null;
  }
}
